import { addMinutes, isBefore, max, min } from 'date-fns'

import {
  FulfilmentFilterWhenType,
  FulfilmentFilterWhenPreorder,
  FulfilmentFilterWhenAnytime,
  FulfilmentFilterWhenASAP,
} from '@src/hooks/useFulfilmentFilter/validation'
import { OutletFulfilmentContextData } from '../context/types'

// calculates the collection preorder time from the current fulfilment filter
// (attempts to find the first available time today or on the date selected by the customer)
// returns null if it should be asap or there are no available times
export const calculateCollectionPreorderDateTime = ({
  outlet,
  fulfilmentFilterWhen,
}: {
  outlet: Pick<
    OutletFulfilmentContextData,
    | 'prepTime'
    | 'isOpen'
    | 'nextOpenDate'
    | 'collectionPreorderTimes'
    | 'orderability'
  >
  fulfilmentFilterWhen:
    | Zod.infer<typeof FulfilmentFilterWhenAnytime>
    | Zod.infer<typeof FulfilmentFilterWhenASAP>
    | Zod.infer<typeof FulfilmentFilterWhenPreorder>
}): Date | null => {
  if (
    // customer selected preorder and outlet has some preordering available
    (fulfilmentFilterWhen.type === FulfilmentFilterWhenType.PREORDER &&
      outlet.orderability.collection.preorder.isAvailableForSelectedDate) ||
    // customer selected ANYTIME or ASAP but asap is not available
    ([FulfilmentFilterWhenType.ANYTIME, FulfilmentFilterWhenType.ASAP].includes(
      fulfilmentFilterWhen.type
    ) &&
      !outlet.orderability.collection.asap.isAvailableNow)
  ) {
    // calculate best preorder window based on customer's selected preorder date / now + prep / closeduntil + prep
    const earliestPossiblePreorderTime = addMinutes(new Date(), outlet.prepTime)
    const earliestPreorderTime =
      fulfilmentFilterWhen.type === FulfilmentFilterWhenType.PREORDER
        ? max([fulfilmentFilterWhen.preorderDate, earliestPossiblePreorderTime])
        : earliestPossiblePreorderTime
    const availablePreorderTimes = outlet.collectionPreorderTimes.filter(
      collectionPreorderTime =>
        !isBefore(collectionPreorderTime, earliestPreorderTime)
    )

    return availablePreorderTimes.length > 0
      ? min(availablePreorderTimes)
      : null
  }

  // default to asap
  return null
}
